import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getPublicAppOrigin } from "@/lib/publicAppUrl";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";

/**
 * Self-service password reset: Supabase sends the recovery email;
 * the link redirects to `/reset-password` (same handler as `/recover-password`).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Enter your email address.");
      return;
    }

    const origin = getPublicAppOrigin();
    if (!origin) {
      setError("App URL is not configured. Set VITE_PUBLIC_APP_URL or open this page in the browser.");
      return;
    }

    const redirectTo = `${origin}/reset-password`;

    setLoading(true);
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo,
      });
      if (resetErr) {
        setError(resetErr.message);
        return;
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-4">
      <Card className="w-full max-w-md border border-[var(--color-border-muted)] bg-[var(--color-surface)] p-6">
        <p className="font-[var(--font-display)] text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-highlight-blue)]">
          Fit2Lift account
        </p>
        <h1 className="mt-2 font-[var(--font-display)] text-xl font-bold uppercase tracking-[0.04em] text-[var(--color-text)]">
          Forgot password
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-muted)]">
          Enter the email you use to sign in. If an account exists, we will send a link to reset your password.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {sent ? (
            <Alert variant="success" role="status">
              Check your email for a reset link. It may take a minute to arrive. The link opens our reset page so you can
              choose a new password.
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="error" role="alert">
              {error}
            </Alert>
          ) : null}

          {!sent ? (
            <>
              <div>
                <Label htmlFor="forgot-email" className="text-[var(--color-text-subtle)]">
                  Email address
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                  className="mt-1"
                  disabled={loading}
                />
              </div>
              <Button type="submit" loading={loading} className="mt-2 w-full justify-center">
                Send reset link
              </Button>
            </>
          ) : (
            <div className="mt-2 text-center">
              <Link to="/login" className="text-sm font-[var(--font-display)] text-[var(--color-signal-blue)] hover:underline">
                Back to sign in
              </Link>
            </div>
          )}
        </form>

        {!sent ? (
          <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
            <Link to="/login" className="text-[var(--color-signal-blue)] hover:underline">
              Back to sign in
            </Link>
          </p>
        ) : null}
      </Card>
    </div>
  );
}
